import { useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { LANGUAGES } from '../intl/i18n.js';
import { useSession } from './SessionProvider.js';

/**
 * Sign in, and accept an invitation.
 *
 * The language picker sits here, before authentication, deliberately: an
 * Arabic-speaking estimator should not have to read English to get in (FR51).
 *
 * There is no "create a shop" path. A shop is created by an administrator, who
 * invites people to it; signing up with an invited address claims the
 * invitation and the membership appears. Someone who signs up uninvited gets a
 * real account and no shop — which the app has a screen for, because that is
 * every invited user in the seconds before their invitation is claimed.
 */

type Mode = 'sign_in' | 'accept';

export function SignIn() {
  const { t, setLanguage, session } = useSession();
  const [mode, setMode] = useState<Mode>('sign_in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lang, setLang] = useState('en');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);

    if (mode === 'sign_in') {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      // Deliberately not "no account with that email" — that tells an attacker
      // which addresses exist.
      if (err) setError('That email and password do not match an account.');
      setBusy(false);
      return;
    }

    // Supabase enforces its own minimum, but failing at the server after a
    // round trip on a bad connection is a worse way to learn it.
    if (password.length < 8) {
      setError('Choose a password of at least 8 characters.');
      setBusy(false);
      return;
    }

    const { data, error: err } = await supabase.auth.signUp({ email, password });
    if (err) {
      setError(err.message);
      setBusy(false);
      return;
    }

    // No session back means confirmation is on and an email is on its way.
    // Saying so is the difference between "it worked" and "nothing happened".
    if (!data.session) {
      setNotice(
        `Check ${email} for a confirmation link. Your shop appears once you confirm.`,
      );
    }
    setBusy(false);
  }

  function pickLanguage(code: string) {
    setLang(code);
    document.documentElement.setAttribute('lang', code);
    document.documentElement.setAttribute(
      'dir', LANGUAGES.find((l) => l.code === code)?.isRtl ? 'rtl' : 'ltr');
    if (session) void setLanguage(code);
  }

  const accepting = mode === 'accept';

  return (
    <div className="signin">
      <form className="signin-form" onSubmit={submit}>
        <div className="mark">Estimator</div>
        <p className="muted">
          {accepting
            ? 'You were invited. Choose a password and your shop appears.'
            : 'Sign in to your shop.'}
        </p>

        <label className="field">
          <span>Email</span>
          <input type="email" autoComplete="username" required
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>

        <label className="field">
          <span>{accepting ? 'Choose a password' : 'Password'}</span>
          <input type="password" required minLength={accepting ? 8 : undefined}
                 autoComplete={accepting ? 'new-password' : 'current-password'}
                 value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>

        {error && <p className="error" role="alert">{error}</p>}
        {notice && <p className="notice-line" role="status">{notice}</p>}

        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? '…' : accepting ? 'Create my account' : t('Sign in')}
        </button>

        <button type="button" className="linkish"
                onClick={() => { setMode(accepting ? 'sign_in' : 'accept');
                                 setError(null); setNotice(null); }}>
          {accepting
            ? 'I already have an account'
            : 'I was invited and need to set a password'}
        </button>

        <div className="lang-picker">
          <div className="lang-label">🌐 {t('Language')}</div>
          <div className="chips">
            {LANGUAGES.map((l) => (
              <button key={l.code} type="button"
                      aria-pressed={l.code === lang}
                      onClick={() => pickLanguage(l.code)}>
                {l.nativeName}
              </button>
            ))}
          </div>
        </div>
      </form>

      <aside className="pitch">
        <h2>Walk the car.<br />The app does the typing.</h2>
        <p>
          Photograph the damage and it proposes what is hurt.
          <b> You confirm every line</b> — nothing becomes money without you.
        </p>
      </aside>
    </div>
  );
}
